import {
  prepareElementsForExport,
  restoreElementsAfterExport,
  getFullContentDimensions,
  prepareClonedDocumentForExport,
  injectExportStyles,
  removeExportStyles
} from './exportHelpers';
import { IProjectMetrics } from '../types';
import { PostIt } from '../types';

/**
 * Exporta o canvas para PDF em formato A3 com relatório Lean
 */
export const exportCanvasToA3 = async (
  canvasElement: HTMLElement,
  projectName: string = 'Workflow',
  metrics: IProjectMetrics,
  postIts: PostIt[] = []
): Promise<void> => {
  let savedStyles: ReturnType<typeof prepareElementsForExport> = [];
  let exportStyleElement: HTMLStyleElement | null = null;

  try {
    // Importar dinamicamente
    const html2canvas = (await import('html2canvas')).default;
    const { jsPDF } = await import('jspdf');

    // Injetar estilos CSS temporários para exportação
    exportStyleElement = injectExportStyles();

    // Obter dimensões completas do conteúdo
    const { width, height } = getFullContentDimensions(canvasElement);

    // Criar canvas da imagem
    const canvas = await html2canvas(canvasElement, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      logging: false,
      allowTaint: true,
      scrollX: 0,
      scrollY: 0,
      x: 0,
      y: 0,
      width: width,
      height: height,
      windowWidth: width,
      windowHeight: height,
      onclone: (clonedDoc) => {
        prepareClonedDocumentForExport(clonedDoc, 'canvas-content');
        const clonedCanvasContent = clonedDoc.querySelector('#canvas-content') as HTMLElement;
        if (clonedCanvasContent) {
          clonedCanvasContent.style.transform = 'none';
          clonedCanvasContent.style.width = `${width}px`;
          clonedCanvasContent.style.height = `${height}px`;
          savedStyles = prepareElementsForExport(clonedCanvasContent);
        }
      }
    });

    // Criar PDF A3 em paisagem (420mm x 297mm)
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a3'
    });

    // Dimensões A3 em mm
    const pdfWidth = 420;
    const pdfHeight = 297;

    // Converter pixels para mm (assumindo 96 DPI: 1px = 0.264583mm)
    const imageWidthMM = (canvas.width * 0.264583);
    const imageHeightMM = (canvas.height * 0.264583);

    // Calcular escala para caber no PDF mantendo proporção
    const scaleX = (pdfWidth - 20) / imageWidthMM; // 10mm de margem de cada lado
    const scaleY = (pdfHeight - 60) / imageHeightMM; // 30mm no topo e 30mm na base para relatório
    const scale = Math.min(scaleX, scaleY, 1); // Não aumentar além do original

    const scaledWidth = imageWidthMM * scale;
    const scaledHeight = imageHeightMM * scale;

    // Centralizar imagem
    const x = (pdfWidth - scaledWidth) / 2;
    const y = 10; // Margem superior

    // Adicionar imagem do canvas
    const imgData = canvas.toDataURL('image/png');
    pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);

    // === SE!ÒO DE RELATRIO A3 ===
    const reportY = y + scaledHeight + 5;

    // Título
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('RELATRIO A3 - DIAGNSTICO LEAN', pdfWidth / 2, reportY, { align: 'center' });

    let currentY = reportY + 10;

    // KPIs Principais
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('KPIs PRINCIPAIS', 10, currentY);
    currentY += 8;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');

    // RTY (Rolled Throughput Yield)
    const rtyValue = parseFloat(metrics.rolledThroughputYield || '100');
    const rtyColor = rtyValue >= 0.8 ? [34, 197, 94] : rtyValue >= 0.6 ? [251, 191, 36] : [239, 68, 68];
    pdf.setTextColor(...rtyColor);
    pdf.text(`RTY (Rolled Throughput Yield): ${(rtyValue * 100).toFixed(1)}%`, 15, currentY);

    // Eficiência de Fluxo
    const efficiencyValue = parseFloat(metrics.flowEfficiency || '0');
    const efficiencyColor = efficiencyValue >= 0.7 ? [34, 197, 94] : efficiencyValue >= 0.5 ? [251, 191, 36] : [239, 68, 68];
    pdf.setTextColor(...efficiencyColor);
    pdf.text(`Eficiência de Fluxo: ${(efficiencyValue * 100).toFixed(1)}%`, 15, currentY + 5);

    // Custo da Fábrica Oculta
    const hiddenFactoryCostValue = parseFloat(metrics.hiddenFactoryCost || '0');
    pdf.setTextColor(239, 68, 68);
    pdf.text(`Custo da Fábrica Oculta: R$ ${hiddenFactoryCostValue.toFixed(2)}`, 15, currentY + 10);

    pdf.setTextColor(0, 0, 0);
    currentY += 18;

    // Pontos Críticos
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('PONTOS CRÍTICOS', 10, currentY);
    currentY += 8;

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');

    // Gargalos
    if (metrics.realBottleneck) {
      pdf.setTextColor(239, 68, 68);
      pdf.text(`⬢ Gargalo identificado com carga crítica`, 15, currentY);
      currentY += 5;
    }

    // Retrabalho
    const totalReworkTimeValue = parseFloat(metrics.totalReworkTime || '0');
    if (totalReworkTimeValue > 0) {
      pdf.setTextColor(251, 146, 60);
      pdf.text(`⬢ Tempo de retrabalho: ${totalReworkTimeValue.toFixed(1)} min`, 15, currentY);
      currentY += 5;
    }

    pdf.setTextColor(0, 0, 0);
    currentY += 5;

    // Plano de Ação (Post-its do Kaizen Log)
    if (postIts.length > 0) {
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('PLANO DE A!ÒO (KAIZEN LOG)', 10, currentY);
      currentY += 8;

      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');

      let colX = 10;
      let itemsInCol = 0;
      const maxItemsPerCol = 8;

      postIts.slice(0, 15).forEach((postIt, index) => {
        if (itemsInCol >= maxItemsPerCol) {
          colX += 130;
          currentY = reportY + 18;
          itemsInCol = 0;
        }

        const typeLabel = postIt.type === 'problem' ? 'PROBLEMA' :
                         postIt.type === 'action' ? 'A!ÒO' :
                         postIt.type === 'idea' ? 'ID0IA' :
                         postIt.type === 'data' ? 'DADO' : 'RISCO';

        pdf.setFont('helvetica', 'bold');
        pdf.text(`${typeLabel}:`, colX, currentY);
        pdf.setFont('helvetica', 'normal');

        // Quebrar texto longo
        const text = postIt.text || '';
        const maxWidth = 120;
        const lines = pdf.splitTextToSize(text, maxWidth);
        lines.forEach((line: string, i: number) => {
          pdf.text(line, colX + 5, currentY + 4 + (i * 4));
        });

        currentY += 8 + (lines.length * 4);
        itemsInCol++;
      });
    }

    // Restaurar estilos
    if (exportStyleElement) {
      removeExportStyles(exportStyleElement);
    }
    restoreElementsAfterExport(savedStyles);

    // Salvar PDF
    pdf.save(`${projectName || 'Workflow'}_A3_${new Date().toISOString().split('T')[0]}.pdf`);

  } catch (error) {
    console.error('Erro ao exportar para A3:', error);
    alert('Erro ao exportar relatório A3. Verifique o console para mais detalhes.');

    // Restaurar estilos em caso de erro
    if (exportStyleElement) {
      removeExportStyles(exportStyleElement);
    }
    restoreElementsAfterExport(savedStyles);
  }
};



